import React, { forwardRef, useEffect, useRef, useState } from 'react';

const pad2 = (n) => String(n).padStart(2, '0');

const isValidDateParts = (dd, mm, yyyy) => {
  if (yyyy < 1000 || yyyy > 9999) return false;
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  const date = new Date(yyyy, mm - 1, dd);
  return date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd;
};

const isoToDmy = (iso) => {
  if (!iso || typeof iso !== 'string') return '';
  const normalizedIso = iso.slice(0, 10);
  const m = normalizedIso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (!isValidDateParts(dd, mm, yyyy)) return '';
  return `${pad2(dd)}/${pad2(mm)}/${yyyy}`;
};

const dmyToIso = (dmy) => {
  if (!dmy || typeof dmy !== 'string') return '';
  const m = dmy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (!isValidDateParts(dd, mm, yyyy)) return '';
  return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
};

const normalizeTypedDate = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const StrictDateInput = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    min,
    max,
    name,
    id,
    ...rest
  } = props;

  const textInputRef = useRef(null);
  const nativePickerRef = useRef(null);

  const focusedRef = useRef(false);
  const [textValue, setTextValue] = useState(() => isoToDmy(value));

  useEffect(() => {
    if (!focusedRef.current) setTextValue(isoToDmy(value));
  }, [value]);

  const emitIsoChange = (isoValue) => {
    if (!onChange) return;
    const eventLike = {
      target: { value: isoValue, name, id },
      currentTarget: { value: isoValue, name, id },
    };
    onChange(eventLike);
  };

  const handleChange = (e) => {
    const nextText = normalizeTypedDate(e.target.value);
    setTextValue(nextText);

    if (nextText === '') {
      emitIsoChange('');
      return;
    }

    const iso = dmyToIso(nextText);
    if (!iso) return;
    if (min && iso < min) return;
    if (max && iso > max) return;
    emitIsoChange(iso);
  };

  const handleFocus = (e) => {
    focusedRef.current = true;
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    const iso = dmyToIso(textValue);

    if (!textValue) {
      setTextValue('');
    } else if (iso) {
      setTextValue(isoToDmy(iso));
      emitIsoChange(iso);
    } else {
      // Revert invalid text to last valid controlled value.
      setTextValue(isoToDmy(value));
    }

    if (onBlur) onBlur(e);
  };

  const handleNativePickerChange = (e) => {
    const iso = e.target.value || '';
    setTextValue(isoToDmy(iso));
    emitIsoChange(iso);
    if (textInputRef.current) textInputRef.current.focus();
  };

  const openCalendar = () => {
    if (rest.disabled || rest.readOnly) return;
    const picker = nativePickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') {
      picker.showPicker();
    } else {
      picker.focus();
      picker.click();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: rest.style?.width || '100%' }}>
      <input
        {...rest}
        ref={(node) => {
          textInputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder || 'DD/MM/YYYY'}
        value={textValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ ...(rest.style || {}), width: '100%', paddingRight: 28 }}
      />

      <input
        ref={nativePickerRef}
        type="date"
        value={dmyToIso(textValue)}
        min={min}
        max={max}
        onChange={handleNativePickerChange}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 22,
          height: 22,
          opacity: 0,
          cursor: rest.disabled || rest.readOnly ? 'not-allowed' : 'pointer',
          pointerEvents: rest.disabled || rest.readOnly ? 'none' : 'auto',
          border: 'none',
          padding: 0,
          margin: 0,
        }}
      />

      <button
        type="button"
        onClick={openCalendar}
        disabled={rest.disabled || rest.readOnly}
        aria-label="Open calendar"
        style={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          border: 'none',
          background: 'transparent',
          cursor: rest.disabled || rest.readOnly ? 'not-allowed' : 'pointer',
          opacity: rest.disabled ? 0.45 : 0.8,
          fontSize: 14,
          lineHeight: 1,
          padding: 2,
          zIndex: 2,
        }}
      >
        📅
      </button>
    </div>
  );
});

StrictDateInput.displayName = 'StrictDateInput';

export default StrictDateInput;